/**
 * Words that are valid guesses but should not appear in a standard answer.
 *
 * Version this list instead of silently changing it once a Daily schedule is
 * active. That keeps every published Daily puzzle stable and reproducible.
 */
export const DISALLOWED_ANSWER_WORDS_V1 = [
  // Archaic, specialist, rare plural, or crossword-heavy vocabulary.
  'apace',
  'bergs',
  'boles',
  'clime',
  'dicta',
  'ducal',
  'fakir',
  'gamin',
  'laity',
  'oakum',
  'odium',
  'pasha',
  'podia',
  'rheum',
  'scull',
  'sibyl',
  'stile',
  'tempi',

  // Terms that are needlessly loaded, stigmatizing, or awkward for a
  // general-audience puzzle even when they have a legitimate dictionary use.
  'crone',
  'dykes',
  'leper',
  'lynch',
  'prude',
  'semen',
  'sperm',
  'spook',

  // Informal, abbreviated, or locale-dependent spellings. They remain valid
  // guesses; answers use the clearest standard form for this US-English game.
  'enrol',
  'eying',
  'gotta',
  'kinda',
  'mamma',
  'momma',
  'sabre',
  'temps',
] as const

/**
 * Stable SOLUTIONS indexes approved for standard Daily and Practice
 * play. Keeping explicit indexes (rather than filtering at runtime) prevents a
 * later wording-policy edit from remapping already scheduled Daily puzzles.
 */
export const CURATED_SOLUTION_INDEXES_V1 = [
  2, 3, 5, 6, 7, 8, 10, 11, 12, 13, 15, 16, 17, 18, 20, 23, 24, 25, 26, 27, 28,
  29, 31, 33, 34, 35, 36, 37, 38, 40, 41, 42, 43, 44, 47, 48, 49, 50, 51, 52,
  53, 54, 55, 56, 57, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 73, 74,
  75, 76, 77, 80, 82, 83, 84, 85, 86, 87, 88, 90, 91, 92, 93, 94, 96, 97, 98,
  99, 103, 104, 105, 106, 108, 109, 111, 112, 113, 114, 115, 117, 118, 119, 121,
  122, 123, 125, 126, 127, 128, 130, 131, 133, 134, 135, 137, 138, 140, 141,
  143, 144, 145, 147, 148, 151, 152, 154, 155, 156, 157, 158, 159, 160, 161,
  162, 164, 165, 166, 167, 168, 169, 170, 173, 174, 175, 176, 177, 178, 180,
  181, 183, 184, 186, 187, 189, 190, 192, 193, 195, 197, 198, 200, 201, 202,
  203, 204, 205, 206, 207, 208, 209, 210, 212, 213, 215, 216, 217, 218, 219,
  220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 232, 233, 234,
  235, 236, 238, 239, 240, 241, 244, 247, 248, 249, 251, 252, 253, 257, 260,
  261, 264, 265, 266, 267, 268, 269, 270, 271, 272, 273, 274, 275, 276, 277,
  278, 279, 280, 281, 282, 283, 284, 285, 286, 287, 288, 289, 290, 291, 292,
  293, 294, 295, 296, 297, 298, 299, 300,
] as const

// The original catalog is immutable for historical scheduling and save IDs.
export const LEGACY_SOLUTION_COUNT = 266

// V1 includes the immutable legacy catalog plus 35 manually reviewed,
// append-only additions. Later candidates belong to a new scheduled version.
export const SOLUTION_CATALOG_V1_COUNT = 301

// 13 August 2026. This future boundary avoids changing an active Daily puzzle
// for players whose local time zone has already advanced to 12 August.
export const DAILY_CURATED_CUTOVER_INDEX = 1685

const curatedIndexSet = new Set<number>(CURATED_SOLUTION_INDEXES_V1)

export const isCuratedSolutionIndex = (index: number) =>
  Number.isInteger(index) && curatedIndexSet.has(index)

/** Resolve an absolute Daily number to its stable original catalog index. */
export const getDailySolutionCatalogIndex = (dailyIndex: number) => {
  if (!Number.isInteger(dailyIndex) || dailyIndex < 0) {
    throw new Error('Invalid index')
  }

  if (dailyIndex < DAILY_CURATED_CUTOVER_INDEX) {
    return dailyIndex % LEGACY_SOLUTION_COUNT
  }

  const curatedOffset = dailyIndex - DAILY_CURATED_CUTOVER_INDEX
  return CURATED_SOLUTION_INDEXES_V1[
    curatedOffset % CURATED_SOLUTION_INDEXES_V1.length
  ]
}
